import React, { useState } from 'react';
import { Dialog, DialogContent, Stepper, Step, StepLabel, Box, Backdrop } from '@mui/material';

import PersonalInfoStep from '../../register/PersonalInfoStep';
import TeachingInfoStep from '../../register/TeachingInfoStep';
import ProfessionalDetailsStep from '../../register/ProfessionalDetailsStep';
import AdditionalInfoStep from '../../register/AdditionalInfoStep';
import BorderedButton from '../../common/buttons/BorderedButton';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import { createProfile } from '../../../services/teacher.service';

const steps = ['Personal Info', 'Teaching Info', 'Professional Details', 'Additional Info'];

export default function TeacherStepperModal({ open, onComplete, user }) {
  const [activeStep, setActiveStep] = useState(0);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    user_id: user.id,
    dob: '',
    subjects: [],
    medium: [],
    classTypes: [],
    cities: [],
    bio: '',
    introVideoUrl: '',
  });

  const [qualifications, setQualifications] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);

  const handleNext = () => {
    setFieldErrors({});
    setSubmitError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (activeStep !== steps.length - 1) return;

    const payload = {
      ...formData,
      qualifications,
      timeSlots,
    };

    try {
      const res = await createProfile(payload);
      onComplete(res);
    } catch (err) {
      const data = err.response?.data;

      if (err.response?.status === 422 && data?.errors) {
        setFieldErrors(data.errors);
        const errorFields = Object.keys(data.errors);
        const stepFieldMap = {
          0: ['dob'],
          1: ['subjects', 'medium', 'classTypes', 'cities'],
          2: ['bio', 'qualifications', 'introVideoUrl'],
          3: ['timeSlots'],
        };
        for (const [step, fields] of Object.entries(stepFieldMap)) {
          if (fields.some((f) => errorFields.includes(f))) {
            setActiveStep(Number(step));
            break;
          }
        }
      } else {
        setSubmitError(data?.message ?? 'Something went wrong');
      }
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <PersonalInfoStep formData={formData} setFormData={setFormData} errors={fieldErrors} />
        );

      case 1:
        return (
          <TeachingInfoStep formData={formData} setFormData={setFormData} errors={fieldErrors} />
        );

      case 2:
        return (
          <ProfessionalDetailsStep
            formData={formData}
            setFormData={setFormData}
            qualifications={qualifications}
            setQualifications={setQualifications}
            errors={fieldErrors}
          />
        );

      case 3:
        return (
          <AdditionalInfoStep
            formData={formData}
            setFormData={setFormData}
            timeSlots={timeSlots}
            setTimeSlots={setTimeSlots}
            errors={fieldErrors}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      slots={{
        backdrop: Backdrop,
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        },
        paper: {
          sx: {
            background: 'linear-gradient(158deg, rgba(17, 24, 39, 1) 0%, rgba(15, 15, 18, 1) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflowX: 'hidden',
          overflowY: 'auto',
          p: { xs: 2, sm: 3 },
        }}
      >
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-semibold text-white">Let's Set Up Your Teacher Profile</h2>
          <p className="text-sm text-gray-400 mt-1">
            Tell us about your expertise, teaching preferences, and availability so students can
            find you.
          </p>
        </div>

        {/* Global error */}
        {submitError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm text-center">{submitError}</p>
          </div>
        )}

        {/* Field errors summary */}
        {Object.keys(fieldErrors).length > 0 && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm font-semibold mb-1">Please fix the following:</p>
            <ul className="list-disc list-inside space-y-1">
              {Object.values(fieldErrors)
                .flat()
                .map((msg, i) => (
                  <li key={i} className="text-red-400 text-sm">
                    {msg}
                  </li>
                ))}
            </ul>
          </div>
        )}

        {/* Stepper */}
        <Stepper
          activeStep={activeStep}
          sx={{
            mt: 6,
            mb: 4,
            '& .MuiStepLabel-label': {
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              display: { xs: 'none', sm: 'block' },
              '&.Mui-active': { color: '#F6911D' },
              '&.Mui-completed': { color: '#22c55e' },
            },
            '& .MuiStepIcon-root': {
              color: 'rgba(255, 255, 255, 0.2)',
              '&.Mui-active': { color: '#F6911D' },
              '&.Mui-completed': { color: '#22c55e' },
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form>
          <div>
            {renderStep()}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <BorderedButton type="button" disabled={activeStep === 0} onClick={handleBack}>
                Back
              </BorderedButton>

              {activeStep === steps.length - 1 ? (
                <PrimaryButton type="button" onClick={handleSubmit}>
                  Finish
                </PrimaryButton>
              ) : (
                <PrimaryButton type="button" onClick={handleNext}>
                  Next
                </PrimaryButton>
              )}
            </Box>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
